import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

import * as UI from '@/components/componentLibrary'
import { DatePickerDropdownValue } from '@/components/componentLibrary/DatePicker/types'
import { allDropDownOptions } from '@/components/componentLibrary/DatePicker/utils'
import { SpatialPolicy as Policy } from '@/models/spatialPolicy'

import { HideableField } from '../components'
import { EditableFieldProps } from '../types'

import './index.scss'

const DATE_RANGE_TITLE = 'Effective Dates'

export const DateRange = ({ policy, ...props }: { policy: Policy }) => {
  const { t } = useTranslation()

  return (
    <HideableField
      className="policies-table-form-date-range"
      titleText={t('policiesLibrary.formDateRangeTitle', DATE_RANGE_TITLE)}
      {...props}
    >
      <div className="policies-table-form-uneditable-date-range">
        {!(policy.startDate || policy.endDate) && (
          <div>
            <UI.Text styleType="body">
              {t('policiesLibrary.formNoneSelected', 'None Selected')}
            </UI.Text>
          </div>
        )}
        {(policy.startDate || policy.endDate) && (
          <>
            <UI.Icon color="black-1" icon="Calendar" />
            <div>
              <UI.Text styleType="body">
                {policy.startDate?.toLocaleString(DateTime.DATE_SHORT) ||
                  t('policiesLibrary.formNoStart', 'No Start Date')}
              </UI.Text>
            </div>
            <div className="policies-table-form-date-range-divider">
              <UI.Divider color="standard" />
            </div>
            <UI.Icon color="black-1" icon="Calendar" />
            <div>
              <UI.Text styleType="body">
                {policy.endDate?.toLocaleString(DateTime.DATE_SHORT) ||
                  t('policiesLibrary.formNoEnd', 'No End Date')}
              </UI.Text>
            </div>
          </>
        )}
      </div>
    </HideableField>
  )
}

export const EditableDateRange = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}: EditableFieldProps) => {
  const { t } = useTranslation()

  return editable ? (
    <HideableField
      className="policies-table-form-date-range"
      titleText={t('policiesLibrary.formDateRangeTitle', DATE_RANGE_TITLE)}
      {...props}
    >
      <UI.DatePicker
        range={{
          from: policy.startDate?.toJSDate() || null,
          to: policy.endDate?.toJSDate() || null,
        }}
        setRange={range => {
          editPolicy?.(p => {
            p.setStartDate(range.from ? DateTime.fromJSDate(range.from) : undefined)
            p.setEndDate(range.to ? DateTime.fromJSDate(range.to) : undefined)
          })
          // check for errors whenever this is set
          onValidate && onValidate()
        }}
        dropdownOptions={allDropDownOptions.filter(
          (option: DatePickerDropdownValue) => option !== 'thisMonth' && option !== 'lastMonth'
        )}
      />
    </HideableField>
  ) : (
    <DateRange policy={policy} {...props} />
  )
}
